'use client';

// ЖИВОЙ ПРИМЕР В СТАТЬЕ СПРАВКИ (владелец 29.09.2026, статья «Как это работает»):
// разметка `::scene s6` в apps/web/content/help/** ставит в текст ТОТ ЖЕ нарисованный
// телефон, что стоит в мастере и туре, — компонент Scene, в той же серой рамке, ход по
// тапу. Сцены s6–s8 — три показа бота из ролика (22.09.2026): слово в комментарии →
// Телеграм, простой бот, продвинутый бот с цепочкой.
//
// ЧЬИ СЛОВА В ТЕЛЕФОНЕ. Гость и человек без определённой ниши видят стоковых персон
// сцены (Дарья Фитнес, Максим, Анастасия). У вошедшего, чья ниша определена по аккаунту
// (портрет клиента, /api/user/niche), кодовое слово, подарок и реплики — его ниши, лицо
// и ник — его аккаунта: тем же механизмом, что в мастере (personalizeScene), только
// раскладки слотов s6–s8 (wizard-scene-texts.ts) и фразы (`help.scene.*`) свои.
//
// НИША — ТОЛЬКО ИЗ КЭША КАБИНЕТА, БЕЗ ЗАПРОСА (usePersistentPeek по ключу
// `cabinet:niche:<язык>`, тот же, что у useNichePersonal). Страница справки публичная, а
// кука входа httpOnly — с клиента не узнать, вошёл ли человек; обычный usePersistentData
// на 401 гостя увёл бы со статьи на /login (проверено 29.09: гость видел «Вход»). Кэш
// кладёт кабинет (список ботов читает нишу при каждом заходе) и чистит выход из
// аккаунта, поэтому «в кэше есть» = «вошёл на этом устройстве». Плитка пасхалки «Текст»
// в приложении открывает статью в веб-вью с общим хранилищем — кэш тот же.
//
// INVARIANT(scriptera): компонент Scene, useScenes и onboarding.css ради статьи НЕ
// правятся — строится копия дефа снаружи. Причина: сцену делят лендинг, тур и мастер,
// и выглядеть она обязана у всех одинаково. Канон: docs/NATIVE-WIZARD-PLAN.md ТЗ-7 Ш3.
import { useMemo, useSyncExternalStore } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Scene, useScenes, type SceneDef } from '@/components/auth/app-onboarding';
import { giftLead, nicheFromSearch, nichePhrases, nicheScene, nicheSceneTexts, type NicheView, type PersonalProfile } from '@/lib/niche-scene';
import type { NicheApi } from '@/lib/use-niche-personal';
import { usePersistentPeek } from '@/lib/use-persistent-data';
import type { HelpSceneKey, SceneTexts } from '@/lib/wizard-scene-texts';
import type { HelpScenePerson } from '@/lib/help-markdown';
import { assetUrl } from '@/lib/asset-url';
import { withoutSeries } from '@/lib/scene-series';

type PhraseT = (key: string, values?: Record<string, string>) => string;

/** Слова ниши для КАЖДОЙ сцены статьи: шаблоны фраз — словарь `help.scene`, общие
 *  («Отправила в директ», «Напиши {word}…», «Готово, держи!») — `auth.onboarding.pers`. */
function sceneNicheTexts(
  scene: HelpSceneKey | 'g1' | 'g2',
  niche: NicheView,
  gender: string,
  th: PhraseT,
  tp: PhraseT,
): SceneTexts | null {
  const promise = niche.promise;
  switch (scene) {
    case 's6':
      return nicheSceneTexts(niche, {
        post: tp('post', { word: niche.word, promise }),
        pub: tp('pub', { gender }),
        reply: th('s6reply', { promise }),
        // §8.25 п.1: подарок открывает фразу — с заглавной.
        give: th('s6give', { promise: giftLead(niche) }),
      });
    case 's7':
      return {
        ...nicheSceneTexts(niche, { reply: th('s7reply', { promise }), give: tp('give', { promise }) }),
        comment: th('s7ask', { promise }),
      };
    case 's8':
      return {
        ...nicheSceneTexts(niche, { reply: th('s8reply', { promise: giftLead(niche) }), give: th('s8give', { gender, promise }) }),
        msg1: th('s8msg1'),
      };
    case 'g1':
    case 'g2':
      // Сцены цели — те же формальные фразы ниши, что на лендинге и в туре (niche-scene `nichePhrases`).
      return nichePhrases(tp, niche, gender === 'male' || gender === 'female' ? gender : null, scene);
    default:
      return null;
  }
}

/** Копия сцены под человека. Лицо в Телеграме (s8) — тоже его: personalizeScene ставит
 *  tgAva только сценам `chat: 'tg'`, а s8 начинается в Инстаграме и переходит в Телеграм
 *  внутри; без этого под именем клиента осталось бы стоковое фото (Д5: «никогда — чужое
 *  лицо под именем клиента»). */
function personalized(
  def: SceneDef,
  texts: SceneTexts | null,
  profile: PersonalProfile | null,
  nicheKey: string | null = null,
  locale = 'ru',
): SceneDef {
  if (!texts && !profile && !nicheKey) return def;
  const out = nicheScene(def, texts, nicheKey, profile, locale);
  if (profile && def.k === 's8') return { ...out, tgAva: profile.avatar || '' };
  return out;
}

const noop = () => () => {};
const readSearch = () => window.location.search;
const noSearch = () => '';

export function HelpScene({
  scene,
  fixed,
  person,
}: {
  scene: HelpSceneKey | 'g1' | 'g2';
  /** QUICK-START §8.16 (владелец 28.10: «пример с готовкой, а в виджете не готовка»): слова сцены — фразы статьи
   *  (`::scene g1 fixed`), ниша, лицо и случай читателя не подставляются — сцена одна у всех и не меняется. */
  fixed?: SceneTexts;
  /** §8.18: персона закреплённой сцены (ник, подпись, фото поста); лицо — стоковое лицо сцены. */
  person?: HelpScenePerson;
}) {
  const scenes = useScenes();
  const locale = useLocale();
  // Кэш кабинета, без запроса (см. шапку файла); пусто — сцена стоковая.
  const [data] = usePersistentPeek<NicheApi>([`cabinet:niche:${locale}`]);
  // НИША ИЗ АДРЕСА (ТЗ HELP-WARM-FIX §5, претензия владельца 26.10.2026 «фото не грузится в iOS»): островок статьи в
  // приложении открыт без куки и без кэша кабинета — сервер страниц кладёт нишу и лицо человека в адрес (`n.*`, `p.*`,
  // niche-scene.ts `nicheQueryOf`). Есть в адресе — главнее кэша; нет — как раньше.
  const search = useSyncExternalStore(noop, readSearch, noSearch);
  const fromUrl = useMemo(() => nicheFromSearch(search), [search]);
  const nicheKey = data?.nicheKey ?? data?.suggest?.nicheKey ?? null;
  const niche = fromUrl?.niche ?? (nicheKey ? (data?.niches.find((n) => n.key === nicheKey) ?? null) : null);
  const profile = fromUrl?.profile ?? data?.profile ?? null;
  const th = useTranslations('help.scene');
  const tp = useTranslations('auth.onboarding.pers');
  const base = scenes.find((s) => s.k === scene) ?? null;
  const def = useMemo(() => {
    if (!base) return null;
    if (fixed) {
      // §8.22 не касается статей: сообщения закреплённой сцены — фразы статьи, без выборки набора.
      const out = withoutSeries(personalized(base, fixed, null, null, locale));
      if (!person) return out;
      // Тот же порядок, что у ниши (niche-scene `nicheScene`): ник автора поста, подпись — если она у сцены есть, фото поста.
      return {
        ...out,
        ...(person.nick && base.nick ? { nick: person.nick } : {}),
        ...(person.name && base.name ? { name: person.name } : {}),
        ...(person.photo ? { postImg: assetUrl(person.photo) } : {}),
      };
    }
    const gender = profile?.gender ?? 'other';
    const texts = niche ? sceneNicheTexts(scene, niche, gender, th, tp) : null;
    return personalized(base, texts, profile, niche?.key ?? null, locale);
  }, [base, scene, niche, profile, th, tp, locale, fixed, person]);
  if (!def) return null;
  return (
    <div className="help-scene" data-scene={scene}>
      <Scene def={def} on active still replay />
    </div>
  );
}
